import type { LngLat, TemplateDetail } from "../shared/types.ts";
import type { UserPieceData } from "../shared/user-piece.ts";
import { parseUserPieceData } from "../shared/user-piece.ts";
import type { NearbyQuest, QuestDetail } from "./quest/types.ts";

const DB_NAME = "mapus-piece-store";
const DB_VERSION = 3;
const STORE = "pieces";
const USER_STORE = "user-pieces";
const QUEST_STORE = "quests";
const NEARBY_STORE = "nearby";
const MAX_ENTRIES = 400;
const MAX_NEARBY_ENTRIES = 100;

/** One locally cached Piece (path is geometry on the Piece, not a separate query unit). */
export type CachedPiece = {
  id: string;
  title: string;
  /** Line geometry used only for drawing after Piece-level bbox hit. */
  path: LngLat[];
  minLat: number;
  minLng: number;
  maxLat: number;
  maxLng: number;
  lastAccessMs: number;
};

/** A published quest's public detail, as the server sent it. */
export type CachedQuest = { id: string; quest: QuestDetail; savedMs: number };

/** One nearby list, keyed by the grid-snapped query. */
export type CachedNearby = { key: string; quests: NearbyQuest[]; savedMs: number };

export type BBox = {
  minLat: number;
  minLng: number;
  maxLat: number;
  maxLng: number;
};

export type LocalUserPiece = {
  id: string;
  name: string;
  data: UserPieceData;
  updatedAt: string;
  /** False until a successful server save. */
  savedToServer: boolean;
  /** Account that owns the server copy. Missing on signed-out drafts and older records. */
  ownerId?: string;
};

function bboxOf(coords: LngLat[]): Pick<CachedPiece, "minLat" | "minLng" | "maxLat" | "maxLng"> {
  let minLat = Infinity;
  let minLng = Infinity;
  let maxLat = -Infinity;
  let maxLng = -Infinity;
  for (const [lng, lat] of coords) {
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  }
  return { minLat, minLng, maxLat, maxLng };
}

function intersects(a: BBox, b: BBox): boolean {
  return a.minLat <= b.maxLat && a.maxLat >= b.minLat && a.minLng <= b.maxLng && a.maxLng >= b.minLng;
}

/**
 * Local IndexedDB store keyed by Piece.
 * put() after Piece detail download; piecesInBbox() for viewport overlay (no server).
 */
export class PieceStore {
  private dbPromise: Promise<IDBDatabase> | null = null;

  private open(): Promise<IDBDatabase> {
    if (this.dbPromise) return this.dbPromise;
    this.dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: "id" });
        }
        if (!db.objectStoreNames.contains(USER_STORE)) {
          db.createObjectStore(USER_STORE, { keyPath: "id" });
        }
        if (!db.objectStoreNames.contains(QUEST_STORE)) {
          db.createObjectStore(QUEST_STORE, { keyPath: "id" });
        }
        if (!db.objectStoreNames.contains(NEARBY_STORE)) {
          db.createObjectStore(NEARBY_STORE, { keyPath: "key" });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error("indexedDB open failed"));
    });
    return this.dbPromise;
  }

  async put(detail: TemplateDetail): Promise<void> {
    const coords = detail.geometry?.coordinates;
    if (!coords || coords.length < 2) return;
    const path = coords.map((c) => [c[0], c[1]] as LngLat);
    const box = bboxOf(path);
    const row: CachedPiece = {
      id: detail.id,
      title: detail.title,
      path,
      ...box,
      lastAccessMs: Date.now(),
    };
    const db = await this.open();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(row);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("put failed"));
    });
    await this.trim();
  }

  /** Pieces whose stored bbox intersects the viewport (Piece-unit query). */
  async piecesInBbox(viewport: BBox): Promise<CachedPiece[]> {
    const db = await this.open();
    const all = await new Promise<CachedPiece[]>((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => resolve((req.result as CachedPiece[]) ?? []);
      req.onerror = () => reject(req.error ?? new Error("getAll failed"));
    });
    const now = Date.now();
    const hits = all.filter((row) =>
      row.path.length >= 2 &&
      intersects(viewport, {
        minLat: row.minLat,
        minLng: row.minLng,
        maxLat: row.maxLat,
        maxLng: row.maxLng,
      }),
    );
    if (hits.length) {
      void this.touch(hits.map((h) => h.id), now);
    }
    return hits;
  }

  private async touch(ids: string[], now: number): Promise<void> {
    const db = await this.open();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      for (const id of ids) {
        const getReq = store.get(id);
        getReq.onsuccess = () => {
          const row = getReq.result as CachedPiece | undefined;
          if (row) {
            row.lastAccessMs = now;
            store.put(row);
          }
        };
      }
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("touch failed"));
    });
  }

  private async trim(): Promise<void> {
    const db = await this.open();
    const all = await new Promise<CachedPiece[]>((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => resolve((req.result as CachedPiece[]) ?? []);
      req.onerror = () => reject(req.error ?? new Error("trim getAll failed"));
    });
    if (all.length <= MAX_ENTRIES) return;
    all.sort((a, b) => a.lastAccessMs - b.lastAccessMs);
    const drop = all.slice(0, all.length - MAX_ENTRIES);
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      for (const row of drop) store.delete(row.id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("trim delete failed"));
    });
  }

  /** Drop every cached public Piece, quest detail and nearby list. Your own drafts stay. */
  async clearCache(): Promise<void> {
    await this.clearStores([STORE, QUEST_STORE, NEARBY_STORE]);
  }

  /** Drop the old public Piece copies only. */
  async clearPieceCache(): Promise<void> {
    await this.clearStores([STORE]);
  }

  async clearNearby(): Promise<void> {
    await this.clearStores([NEARBY_STORE]);
  }

  private async clearStores(names: string[]): Promise<void> {
    const db = await this.open();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(names, "readwrite");
      for (const name of names) tx.objectStore(name).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("cache clear failed"));
    });
  }

  async getQuest(id: string): Promise<CachedQuest | null> {
    return ((await this.getRow(QUEST_STORE, id)) as CachedQuest | undefined) ?? null;
  }

  async putQuest(row: CachedQuest): Promise<void> {
    await this.putRow(QUEST_STORE, row);
    await this.trimStore(QUEST_STORE, MAX_ENTRIES);
  }

  async deleteQuest(id: string): Promise<void> {
    const db = await this.open();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(QUEST_STORE, "readwrite");
      tx.objectStore(QUEST_STORE).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("quest delete failed"));
    });
  }

  async getNearby(key: string): Promise<CachedNearby | null> {
    return ((await this.getRow(NEARBY_STORE, key)) as CachedNearby | undefined) ?? null;
  }

  async putNearby(row: CachedNearby): Promise<void> {
    await this.putRow(NEARBY_STORE, row);
    await this.trimStore(NEARBY_STORE, MAX_NEARBY_ENTRIES);
  }

  private async getRow(store: string, key: string): Promise<unknown> {
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const req = db.transaction(store, "readonly").objectStore(store).get(key);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error(`${store} get failed`));
    });
  }

  private async putRow(store: string, row: unknown): Promise<void> {
    const db = await this.open();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(store, "readwrite");
      tx.objectStore(store).put(row);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error(`${store} put failed`));
    });
  }

  /** Oldest saves go first once a store holds more than `max` rows. */
  private async trimStore(store: string, max: number): Promise<void> {
    const db = await this.open();
    const all = await new Promise<Array<{ savedMs: number; id?: string; key?: string }>>((resolve, reject) => {
      const req = db.transaction(store, "readonly").objectStore(store).getAll();
      req.onsuccess = () => resolve(req.result ?? []);
      req.onerror = () => reject(req.error ?? new Error(`${store} trim failed`));
    });
    if (all.length <= max) return;
    all.sort((a, b) => a.savedMs - b.savedMs);
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(store, "readwrite");
      for (const row of all.slice(0, all.length - max)) tx.objectStore(store).delete((row.id ?? row.key)!);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error(`${store} trim failed`));
    });
  }

  /** Drop one cached public Piece, e.g. after its author deletes it. */
  async delete(id: string): Promise<void> {
    const db = await this.open();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("piece delete failed"));
    });
  }

  async listUserPieces(): Promise<LocalUserPiece[]> {
    const db = await this.open();
    const rows = await new Promise<LocalUserPiece[]>((resolve, reject) => {
      const tx = db.transaction(USER_STORE, "readonly");
      const req = tx.objectStore(USER_STORE).getAll();
      req.onsuccess = () => resolve((req.result as LocalUserPiece[]) ?? []);
      req.onerror = () => reject(req.error ?? new Error("userPieces getAll failed"));
    });
    return rows
      .map(readLocalUserPiece)
      .filter((row): row is LocalUserPiece => row != null)
      .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0));
  }

  async getUserPiece(id: string): Promise<LocalUserPiece | null> {
    const db = await this.open();
    const row = await new Promise<LocalUserPiece | undefined>((resolve, reject) => {
      const tx = db.transaction(USER_STORE, "readonly");
      const req = tx.objectStore(USER_STORE).get(id);
      req.onsuccess = () => resolve(req.result as LocalUserPiece | undefined);
      req.onerror = () => reject(req.error ?? new Error("userPiece get failed"));
    });
    return row ? readLocalUserPiece(row) : null;
  }

  async putUserPiece(piece: LocalUserPiece): Promise<void> {
    const db = await this.open();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(USER_STORE, "readwrite");
      tx.objectStore(USER_STORE).put(piece);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("userPiece put failed"));
    });
  }

  async deleteUserPiece(id: string): Promise<void> {
    const db = await this.open();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(USER_STORE, "readwrite");
      tx.objectStore(USER_STORE).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error("userPiece delete failed"));
    });
  }
}

function readLocalUserPiece(value: unknown): LocalUserPiece | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as {
    id?: unknown;
    name?: unknown;
    data?: unknown;
    updatedAt?: unknown;
    savedToServer?: unknown;
    ownerId?: unknown;
  };
  if (typeof raw.id !== "string" || !raw.id) return null;
  if (typeof raw.name !== "string") return null;
  if (typeof raw.updatedAt !== "string") return null;
  try {
    const data = parseUserPieceData(raw.data);
    return {
      id: raw.id,
      name: raw.name,
      data,
      updatedAt: raw.updatedAt,
      savedToServer: raw.savedToServer === true,
      ...(typeof raw.ownerId === "string" && raw.ownerId ? { ownerId: raw.ownerId } : {}),
    };
  } catch {
    return null;
  }
}

/** The one store the app shares (home, quest cache). */
export const pieceStore = new PieceStore();
